/**
 * COMPTABILITÉ — RÉFÉRENTIELS.
 *
 *     LIEU (hr_location)  →  POINT DE VENTE  →  CAISSE
 *
 * ═══ LE TPE N'EST PAS UNE CAISSE ═══
 *
 * Un TPE circule : il passe du bar de la ROOM 1 à la billetterie un soir de
 * forte affluence. Sa préférence de point de vente PRÉREMPLIT une saisie et
 * CLASSE une ligne ; elle ne décide jamais qu'une transaction appartient à un
 * point de vente. Le rattacher rigidement produirait des écarts fantômes — des
 * recettes imputées à un comptoir fermé cette nuit-là. L'écran le DIT, au lieu
 * de laisser croire le contraire par sa seule mise en page.
 *
 * ═══ ON ARCHIVE, ON NE SUPPRIME JAMAIS ═══
 *
 * Un point de vente, une caisse, un TPE, un compte, un moyen de paiement, un
 * motif d'écart ou une composante ayant servi à une clôture doit rester lisible
 * pour toujours : sans lui, une clôture d'il y a deux ans afficherait des
 * lignes rattachées à un identifiant orphelin. Aucune surface de cet écran
 * n'offre de suppression de ces objets, et il ne doit jamais en apparaître.
 *
 * La SEULE chose qui se retire vraiment est un ALIAS FOURNISSEUR : c'est une
 * règle de lecture, pas une écriture comptable. Rien d'historique ne le cite —
 * la ligne déjà lue garde le libellé brut ET la caisse retenue ce jour-là.
 *
 * ═══ LES NOMENCLATURES SONT ADMINISTRABLES ═══
 *
 * Moyens de paiement, motifs d'écart et composantes de recette se créent, se
 * renomment, s'activent et s'archivent depuis cet écran. Leur IDENTIFIANT, en
 * revanche, se fige dès qu'une écriture le cite : le libellé « Chèque » peut
 * devenir « Chèque bancaire » sans que rien ne bouge, mais CHEQUE ne peut plus
 * devenir CHQ sans faire mentir tout l'historique. L'écran fige le champ et dit
 * pourquoi, plutôt que de laisser saisir un renommage que le serveur refusera.
 *
 * ═══ LES ALIAS FOURNISSEUR SONT EN SECOND RANG ═══
 *
 * « Caisse Bar Planete 1 », « Carte Bleue », « Espèce » sont des mots de
 * fournisseur. Ils apparaissent comme LIBELLÉS RECONNUS, jamais comme nom
 * canonique : la colonne s'appelle « Libellés fournisseur » et le nom de
 * Gestion reste en tête de ligne. Un nom de salle hérité (Salon, Planète,
 * Club) ne peut vivre ici que sous cette forme.
 *
 * ═══ COMMENT ON MODIFIE ═══
 *
 * Aucun tiroir : l'objet en cours d'édition vit à côté de sa liste. Les
 * changements de VALEUR passent par la barre d'enregistrement du design system
 * (une unité d'édition, Ctrl+S, garde de navigation) ; l'archivage et les alias
 * sont des ACTIONS ATOMIQUES : bouton explicite, confirmation, effet immédiat.
 *
 * ROUTES SERVIES (`apps/api/src/routes/finance.ts`) :
 *   GET    /api/v1/finance/references?include_archived&include_inactive&with_usage  finance.reference.read
 *   GET    /api/v1/finance/references/locations                          finance.reference.read
 *   POST   /api/v1/finance/references/points-of-sale|registers|terminals finance.reference.manage (step-up)
 *   POST   /api/v1/finance/references/codes/:famille[/:code/archive]     finance.reference.manage (step-up)
 *   POST   /api/v1/finance/references/:famille/:id/archive               finance.reference.manage (step-up)
 *   POST   /api/v1/finance/references/registers/:id/aliases              finance.reference.manage (step-up)
 *   DELETE /api/v1/finance/references/register-aliases/:id               finance.reference.manage (step-up)
 *   POST   /api/v1/finance/references/payment-method-aliases             finance.reference.manage (step-up)
 *   DELETE /api/v1/finance/references/payment-method-aliases/:id         finance.reference.manage (step-up)
 *   GET    /api/v1/finance/references/bank-accounts                      finance.bank.read
 *   POST   /api/v1/finance/references/bank-accounts[/:id/archive]        finance.bank.manage (step-up)
 */

import { useEffect, useMemo, useState } from 'react'
import { Archive, BookOpen, Info, Plus, Settings2, X } from 'lucide-react'
import { Link } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, Tabs, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Field, SwitchRow, TextInput } from '../admin/form.js'
import { useConfirm } from '../shell/confirm.js'
import { useSaveBar } from '../shell/editing.js'
import { EtatBloc, entier, estObjet, texte, useRessource } from './RevenueShared.js'
import { remplacerRecherche } from './adresse-ecran.js'
import './finance.css'
import './finance-revenue.css'

/* ═════════════════════════ description des collections ═════════════════ */

interface Option { value: string; label: string }

interface ChampDef {
  cle: string
  label: string
  type: 'texte' | 'liste'
  options?: Option[]
  aide?: string
  requis?: boolean
  majuscules?: boolean
  /** Un identifiant déjà cité par une écriture ne se renomme plus (§renommage). */
  figeSiUtilise?: boolean
  /** Exemple montré dans le champ vide. Jamais une valeur par défaut. */
  exemple?: string
  /** Chemin discret vers le référentiel qui alimente cette liste. */
  lienGestion?: { label: string; route: string } | undefined
  /** Ce que dit une liste facultative laissée vide. « Aucun » est parfois trop sec. */
  vide?: string
}

interface AliasBrut { id: string; provider: string; provider_label: string; method_code: string }

interface Contexte {
  lieux: Option[]
  pdv: Option[]
  /**
   * Les caisses, QUALIFIÉES par leur point de vente et leur lieu.
   *
   * « Caisse 1 » ne suffit pas : deux salles peuvent en avoir une. Le libellé
   * porte donc la hiérarchie — mais c'est bien la CAISSE qui est choisie, pas
   * trois sélecteurs en cascade.
   */
  caisses: Option[]
  moyens: Option[]
  aliasMoyens: AliasBrut[]
  /**
   * L'écran Organisation → Lieux est-il atteignable par CETTE personne ?
   *
   * On ne propose jamais un chemin qui finirait sur un refus : un bouton qui
   * mène à une porte fermée est pire que pas de bouton du tout — il fait
   * croire à un droit qu'on n'a pas, puis accuse l'utilisateur de l'avoir
   * cliqué.
   */
  peutVoirLieux: boolean
}

/** Le bloc « Libellés fournisseur » attaché à la ligne ouverte, quand elle en a un. */
interface SectionAlias {
  aide: string
  fournisseurs: Option[]
  liste: Array<{ id: string; provider: string; libelle: string }>
  /** Rend `null` en cas de succès, sinon la phrase du serveur. */
  ajouter: (provider: string, libelle: string) => Promise<string | null>
  retirer: (id: string) => Promise<string | null>
}

interface Collection {
  id: string
  onglet: string
  titre: string
  intro: string
  /** La collection dans la charge utile agrégée. */
  racine: string
  /** Le chemin d'écriture — `null` quand la collection est en lecture seule. */
  segment: string | null
  /** La famille acceptée par la route d'archivage — `null` si non archivable. */
  archiveSegment: string | null
  /** Le droit exigé pour écrire. */
  permission: string
  cle: 'id' | 'code'
  entetes: string[]
  cellules: (o: Record<string, unknown>, c: Contexte) => (string | null)[]
  champs: (c: Contexte) => ChampDef[]
  alias?: (o: Record<string, unknown>, c: Contexte) => SectionAlias
  /** État vide écrit pour CETTE collection, plutôt qu'un « Aucun élément ». */
  videTitre?: string
  videTexte?: string
  /** L'identifiant technique, montré replié sur la ligne ouverte. */
  technique?: (o: Record<string, unknown>) => string | null
}

const KINDS: Option[] = [
  { value: 'BILLETTERIE', label: 'Billetterie' },
  { value: 'BAR', label: 'Bar' },
  { value: 'VESTIAIRE', label: 'Vestiaire' },
  { value: 'TABAC', label: 'Tabac / Cigarettes' },
  { value: 'AUTRE', label: 'Autre' },
]

const CATEGORIES: Option[] = [
  { value: 'BILLETTERIE', label: 'Billetterie' },
  { value: 'BAR', label: 'Bar' },
  { value: 'VESTIAIRE', label: 'Vestiaire' },
  { value: 'OFFERTS', label: 'Offerts' },
  { value: 'FRAIS', label: 'Frais fournisseur' },
  { value: 'AUTRE', label: 'Autre' },
]

/** Les fournisseurs d'un alias de CAISSE (`finance_register_alias`). */
const FOURNISSEURS_CAISSE: Option[] = [
  { value: 'ZAP_SIS', label: 'ZAP' },
  { value: 'SHOTGUN', label: 'Shotgun' },
  { value: 'MANUEL', label: 'Dépôt manuel' },
  { value: 'AUTRE', label: 'Autre' },
]

/**
 * Les fournisseurs d'un alias de MOYEN DE PAIEMENT. La liste n'est pas la même
 * que pour les caisses : la table admet « LEGACY » — le classeur historique —
 * et refuse « MANUEL ». Recopier l'autre liste aurait produit un refus de base
 * illisible au lieu d'un choix impossible.
 */
const FOURNISSEURS_MOYEN: Option[] = [
  { value: 'ZAP_SIS', label: 'ZAP' },
  { value: 'SHOTGUN', label: 'Shotgun' },
  { value: 'LEGACY', label: 'Classeur historique' },
  { value: 'AUTRE', label: 'Autre' },
]

/** Les libellés fournisseur d'une ligne, en une phrase de second rang. */
function libellesReconnus(alias: Array<{ provider: string; libelle: string }>, fournisseurs: Option[]): string | null {
  if (alias.length === 0) return null
  return alias
    .map((a) => `« ${a.libelle} » · ${fournisseurs.find((f) => f.value === a.provider)?.label ?? a.provider}`)
    .join(' · ')
}

/** Les alias de caisse portés par une ligne `registers` de l'API. */
function aliasDeCaisse(o: Record<string, unknown>): Array<{ id: string; provider: string; libelle: string }> {
  const brut = o['aliases']
  if (!Array.isArray(brut)) return []
  const out: Array<{ id: string; provider: string; libelle: string }> = []
  for (const a of brut) {
    if (!estObjet(a)) continue
    const id = texte(a['id']); const provider = texte(a['provider']); const libelle = texte(a['provider_label'])
    if (id === null || provider === null || libelle === null) continue
    out.push({ id, provider, libelle })
  }
  return out
}

/** Les alias qui désignent CE moyen de paiement. */
function aliasDeMoyen(code: string, c: Contexte): Array<{ id: string; provider: string; libelle: string }> {
  return c.aliasMoyens
    .filter((a) => a.method_code === code)
    .map((a) => ({ id: a.id, provider: a.provider, libelle: a.provider_label }))
}

/** Le message d'erreur d'une mutation, ou `null` si elle a réussi. */
async function mutation(methode: string, chemin: string, corps?: unknown): Promise<string | null> {
  const r = await adminMutate<{ error?: string; message?: string }>(methode, chemin, corps)
  if (r.status >= 200 && r.status < 300) return null
  return r.data.message ?? 'Le serveur a refusé cette opération.'
}

const AIDE_CODE = 'L’identifiant voyage dans les exports et les rapports fournisseur : il se fige dès qu’une écriture le cite.'

const COLLECTIONS: Collection[] = [
  {
    id: 'pos', onglet: 'Points de vente', titre: 'Points de vente',
    intro: 'Les points de vente organisent vos recettes par emplacement et par activité. Chaque point de vente peut ensuite contenir une ou plusieurs caisses.',
    videTitre: 'Aucun point de vente',
    videTexte: 'Créez le premier, par exemple Billetterie à l’Accueil ou Bar en ROOM 1.',
    racine: 'points_of_sale', segment: 'points-of-sale', archiveSegment: 'points-of-sale',
    permission: 'finance.reference.manage', cle: 'id',
    entetes: ['Nom', 'Emplacement', 'Activité', 'Caisses'],
    cellules: (o) => [
      texte(o['name']), texte(o['location_name']),
      KINDS.find((k) => k.value === texte(o['kind']))?.label ?? texte(o['kind']),
      typeof o['register_count'] === 'number' ? String(o['register_count']) : null,
    ],
    champs: (c) => [
      { cle: 'name', label: 'Nom', type: 'texte', requis: true, exemple: 'Billetterie principale' },
      {
        cle: 'location_id', label: 'Emplacement', type: 'liste', options: c.lieux, requis: true,
        aide: c.lieux.length === 0
          ? 'Aucun emplacement disponible. Les emplacements se créent dans Équipe → Organisation → Lieux.'
          : 'Où se trouve ce point de vente ?',
        lienGestion: c.peutVoirLieux ? { label: 'Gérer les lieux', route: '/equipe/organisation/lieux' } : undefined,
      },
      { cle: 'kind', label: 'Activité', type: 'liste', options: KINDS, requis: true,
        aide: 'Sert à classer le chiffre d’affaires et les clôtures.' },
    ],
    /* Le code technique n'est plus saisi : il est fabriqué au serveur et ne
       bouge plus ensuite. On le montre quand même, en lecture seule, parce
       qu'il apparaît dans les exports — mais après coup, et replié. */
    technique: (o) => texte(o['code']),
  },
  {
    id: 'caisses', onglet: 'Caisses', titre: 'Caisses',
    intro: 'Une caisse appartient à un point de vente. Les noms que ZAP imprime sont conservés comme libellés fournisseur : ils permettent de reconnaître automatiquement les rapports.',
    videTitre: 'Aucune caisse',
    videTexte: 'Créez la première caisse d’un point de vente.',
    racine: 'registers', segment: 'registers', archiveSegment: 'registers',
    permission: 'finance.reference.manage', cle: 'id',
    entetes: ['Nom', 'Point de vente', 'Libellés fournisseur'],
    cellules: (o) => [
      texte(o['name']), texte(o['point_of_sale_name']),
      libellesReconnus(aliasDeCaisse(o), FOURNISSEURS_CAISSE),
    ],
    champs: (c) => [
      { cle: 'point_of_sale_id', label: 'Point de vente', type: 'liste', options: c.pdv, requis: true },
      { cle: 'name', label: 'Nom', type: 'texte', requis: true, exemple: 'Caisse 1' },
    ],
    technique: (o) => texte(o['code']),
    alias: (o) => ({
      aide: 'Le nom qu’un fournisseur imprime pour cette caisse. Un même libellé ne peut désigner qu’une seule caisse, sinon la lecture d’un rapport deviendrait ambiguë.',
      fournisseurs: FOURNISSEURS_CAISSE,
      liste: aliasDeCaisse(o),
      ajouter: (provider, libelle) => mutation(
        'POST', `/api/v1/finance/references/registers/${texte(o['id']) ?? ''}/aliases`,
        { provider, provider_label: libelle },
      ),
      retirer: (id) => mutation('DELETE', `/api/v1/finance/references/register-aliases/${id}`),
    }),
  },
  {
    id: 'tpe', onglet: 'TPE', titre: 'Terminaux de paiement',
    intro: 'Un TPE possède une caisse habituelle, qui sert à préremplir les clôtures. Il peut être utilisé sur une autre caisse pendant une nuit sans que son affectation change.',
    videTitre: 'Aucun TPE',
    videTexte: 'Ajoutez vos terminaux et associez-les à leur caisse habituelle. Ils pourront être utilisés ailleurs ponctuellement lors d’une clôture.',
    racine: 'terminals', segment: 'terminals', archiveSegment: 'terminals',
    permission: 'finance.reference.manage', cle: 'id',
    entetes: ['Nom', 'Caisse habituelle', 'Référence fournisseur'],
    cellules: (o) => [
      texte(o['name']),
      // Le lieu et le point de vente sont DÉRIVÉS de la caisse : affichés en
      // second rang, jamais ressaisis.
      texte(o['preferred_register_name']) === null
        ? 'Non affecté'
        : [texte(o['preferred_register_name']), texte(o['point_of_sale_name']), texte(o['location_name'])]
            .filter(Boolean).join(' · '),
      texte(o['provider_reference']),
    ],
    champs: (c) => [
      { cle: 'name', label: 'Nom', type: 'texte', requis: true, exemple: 'TPE 01' },
      { cle: 'provider_reference', label: 'Référence fournisseur', type: 'texte',
        aide: 'Facultatif — l’identifiant imprimé sur les relevés du terminal, s’il existe.' },
      {
        cle: 'preferred_register_id', label: 'Caisse habituelle', type: 'liste', options: c.caisses,
        vide: 'Aucune — terminal non affecté',
        aide: 'Facultatif. Sert à préremplir les clôtures : le terminal peut servir ailleurs une nuit sans que cela change.',
      },
    ],
    technique: (o) => texte(o['code']),
  },
  {
    id: 'comptes', onglet: 'Comptes bancaires', titre: 'Comptes bancaires',
    intro: 'Gestion n’émet aucun virement et ne se connecte à aucune banque : elle n’a donc aucune raison de détenir un IBAN complet. Un IBAN masqué suffit à reconnaître le compte.',
    racine: 'bank_accounts', segment: 'bank-accounts', archiveSegment: 'bank-accounts',
    permission: 'finance.bank.manage', cle: 'id',
    entetes: ['Nom', 'IBAN masqué', 'Remises'],
    cellules: (o) => [
      texte(o['name']), texte(o['iban_masked']),
      typeof o['deposit_count'] === 'number' ? String(o['deposit_count']) : null,
    ],
    champs: () => [
      { cle: 'name', label: 'Nom du compte', type: 'texte', requis: true },
      { cle: 'iban_masked', label: 'IBAN masqué', type: 'texte', aide: 'Facultatif, et volontairement partiel : par exemple FR76 •••• •••• 1234.' },
    ],
  },
  {
    id: 'paiements', onglet: 'Moyens de paiement', titre: 'Moyens de paiement',
    intro: 'Ce que la clôture proposera comme moyen d’encaissement. Un moyen inactif reste en base et disparaît des listes de saisie : une liste de six choix dont trois n’ont jamais servi ralentit chaque nuit.',
    racine: 'payment_methods', segment: 'codes/payment-methods', archiveSegment: 'codes/payment-methods',
    permission: 'finance.reference.manage', cle: 'code',
    entetes: ['Code', 'Libellé', 'Libellés fournisseur'],
    cellules: (o, c) => [
      texte(o['code']), texte(o['label']),
      libellesReconnus(aliasDeMoyen(texte(o['code']) ?? '', c), FOURNISSEURS_MOYEN),
    ],
    champs: () => [
      { cle: 'code', label: 'Code', type: 'texte', requis: true, majuscules: true, figeSiUtilise: true, aide: AIDE_CODE },
      { cle: 'label', label: 'Libellé', type: 'texte', requis: true, aide: 'Ce que lit la personne qui clôture. Il se change quand on veut : aucune écriture ne le cite.' },
    ],
    alias: (o, c) => ({
      aide: 'Le mot qu’un fournisseur emploie pour ce moyen — « Carte Bleue » chez ZAP se lit CB chez nous. Aucun rapprochement ne se fait par ressemblance : deux caractères d’écart, et l’argent changerait de colonne.',
      fournisseurs: FOURNISSEURS_MOYEN,
      liste: aliasDeMoyen(texte(o['code']) ?? '', c),
      ajouter: (provider, libelle) => mutation(
        'POST', '/api/v1/finance/references/payment-method-aliases',
        { provider, provider_label: libelle, method_code: texte(o['code']) ?? '' },
      ),
      retirer: (id) => mutation('DELETE', `/api/v1/finance/references/payment-method-aliases/${id}`),
    }),
  },
  {
    id: 'motifs', onglet: 'Motifs d’écart', titre: 'Motifs d’écart',
    intro: 'La liste que verra la personne qui explique un écart à trois heures du matin. Un motif absent de cette liste devient « Autre » — et une explication qui finit toujours en « Autre » n’explique rien.',
    racine: 'discrepancy_reasons', segment: 'codes/discrepancy-reasons', archiveSegment: 'codes/discrepancy-reasons',
    permission: 'finance.reference.manage', cle: 'code',
    entetes: ['Code', 'Libellé'],
    cellules: (o) => [texte(o['code']), texte(o['label'])],
    champs: () => [
      { cle: 'code', label: 'Code', type: 'texte', requis: true, majuscules: true, figeSiUtilise: true, aide: AIDE_CODE },
      { cle: 'label', label: 'Libellé', type: 'texte', requis: true },
    ],
  },
  {
    id: 'composantes', onglet: 'Composantes', titre: 'Composantes de recette',
    intro: 'Ce que les colonnes du chiffre d’affaires signifient. Une composante dit CE QUE C’EST (bar, vestiaire, entrées) — jamais d’où le chiffre vient : confondre les deux est le premier pas vers le double comptage.',
    racine: 'component_types', segment: 'codes/component-types', archiveSegment: 'codes/component-types',
    permission: 'finance.reference.manage', cle: 'code',
    entetes: ['Code', 'Libellé', 'Catégorie'],
    cellules: (o) => [
      texte(o['code']), texte(o['label']),
      CATEGORIES.find((k) => k.value === texte(o['category']))?.label ?? texte(o['category']),
    ],
    champs: () => [
      { cle: 'code', label: 'Code', type: 'texte', requis: true, majuscules: true, figeSiUtilise: true, aide: AIDE_CODE },
      { cle: 'label', label: 'Libellé', type: 'texte', requis: true },
      { cle: 'category', label: 'Catégorie', type: 'liste', options: CATEGORIES, requis: true, aide: 'Ce que la composante MESURE. Deux composantes de même catégorie ne s’additionnent pas sans règle explicite.' },
    ],
  },
]

/* ═════════════════════════ lecture des collections ═════════════════════ */

interface Element {
  id: string
  brut: Record<string, unknown>
  actif: boolean
  archive: boolean
  /** Écritures qui citent ce code — `null` quand le serveur ne l'a pas compté. */
  usage: number | null
}

interface Referentiels {
  collections: Record<string, Element[]>
  aliasMoyens: AliasBrut[]
}

function elementsDe(brut: unknown, cle: 'id' | 'code'): Element[] {
  if (!Array.isArray(brut)) return []
  const out: Element[] = []
  for (const it of brut) {
    if (!estObjet(it)) continue
    const id = texte(it[cle])
    if (id === null) continue
    out.push({
      id, brut: it,
      actif: it['active'] !== false,
      archive: texte(it['archived_at']) !== null,
      usage: entier(it['usage_count']),
    })
  }
  return out
}

function aliasMoyensDe(v: unknown): AliasBrut[] {
  const brut = estObjet(v) ? v['payment_method_aliases'] : null
  if (!Array.isArray(brut)) return []
  const out: AliasBrut[] = []
  for (const a of brut) {
    if (!estObjet(a)) continue
    const id = texte(a['id']); const provider = texte(a['provider'])
    const libelle = texte(a['provider_label']); const code = texte(a['method_code'])
    if (id === null || provider === null || libelle === null || code === null) continue
    out.push({ id, provider, provider_label: libelle, method_code: code })
  }
  return out
}

function lireReferentiels(v: unknown): Referentiels | null {
  if (!estObjet(v)) return null
  const attendues = COLLECTIONS.filter((c) => c.racine !== 'bank_accounts')
  if (!attendues.some((c) => Array.isArray(v[c.racine]))) return null
  const collections: Record<string, Element[]> = {}
  for (const c of attendues) collections[c.racine] = elementsDe(v[c.racine], c.cle)
  return { collections, aliasMoyens: aliasMoyensDe(v) }
}

function lireComptes(v: unknown): Element[] | null {
  if (!estObjet(v) || !Array.isArray(v['bank_accounts'])) return null
  return elementsDe(v['bank_accounts'], 'id')
}

function lireLieux(v: unknown): Option[] | null {
  if (!estObjet(v) || !Array.isArray(v['locations'])) return null
  const out: Option[] = []
  for (const l of v['locations']) {
    if (!estObjet(l)) continue
    const value = texte(l['id']); const label = texte(l['name'])
    if (value === null || label === null) continue
    out.push({ value, label })
  }
  return out
}

/* ═════════════════════ les libellés fournisseur ═══════════════════════ */

/**
 * Le SECOND RANG, rendu. Un alias ne remplace jamais le nom canonique : il vit
 * sous lui, il se lit, il se retire. C'est aussi le seul endroit de l'écran où
 * un mot hérité (« Caisse Bar Planete 1 ») a le droit d'apparaître.
 */
function AliasFournisseur(props: {
  section: SectionAlias
  peutEcrire: boolean
  onChange: () => void
}) {
  const toast = useToast()
  const confirmer = useConfirm()
  const [fournisseur, setFournisseur] = useState(props.section.fournisseurs[0]?.value ?? '')
  const [libelle, setLibelle] = useState('')
  const [enCours, setEnCours] = useState(false)

  const ajouter = async () => {
    const saisi = libelle.trim()
    if (saisi === '' || enCours) return
    setEnCours(true)
    const echec = await props.section.ajouter(fournisseur, saisi)
    setEnCours(false)
    if (echec !== null) { toast('error', echec); return }
    setLibelle('')
    toast('ok', 'Libellé fournisseur ajouté.')
    props.onChange()
  }

  const retirer = async (a: { id: string; libelle: string }) => {
    const ok = await confirmer({
      title: `Retirer « ${a.libelle} » ?`,
      message: 'Ce libellé ne sera plus reconnu dans les prochaines lectures. Les lectures déjà faites gardent le rapprochement qu’elles avaient : rien de comptabilisé ne change.',
      variant: 'sensitive',
      confirmLabel: 'Retirer',
    })
    if (!ok) return
    const echec = await props.section.retirer(a.id)
    if (echec !== null) { toast('error', echec); return }
    toast('ok', 'Libellé fournisseur retiré.')
    props.onChange()
  }

  return (
    <div className="fr-stack">
      <div>
        <h3 className="fr-section__title">Libellés fournisseur</h3>
        <p className="fr-section__hint">{props.section.aide}</p>
      </div>

      {props.section.liste.length === 0 ? (
        <p className="fr-mini">Aucun libellé fournisseur reconnu pour l’instant.</p>
      ) : (
        <div className="fr-facts">
          {props.section.liste.map((a) => (
            <div className="fr-fact" key={a.id}>
              <span className="fr-fact__label">
                « {a.libelle} » · {props.section.fournisseurs.find((f) => f.value === a.provider)?.label ?? a.provider}
              </span>
              {props.peutEcrire ? (
                <button
                  type="button" className="lc-btn lc-btn--ghost lc-btn--sm"
                  onClick={() => { void retirer(a) }}
                  aria-label={`Retirer le libellé ${a.libelle}`}
                >
                  <X size={14} aria-hidden="true" /> Retirer
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {props.peutEcrire ? (
        <div className="fr-fields fr-fields--pair">
          <Field label="Fournisseur">
            <Select
              value={fournisseur}
              options={props.section.fournisseurs}
              onChange={setFournisseur}
              ariaLabel="Fournisseur du libellé"
            />
          </Field>
          <Field label="Libellé imprimé par le fournisseur">
            <TextInput
              value={libelle}
              onChange={setLibelle}
              ariaLabel="Libellé fournisseur"
              placeholder="Carte Bleue"
            />
          </Field>
        </div>
      ) : null}

      {props.peutEcrire ? (
        <div className="fr-actions">
          <button
            type="button" className="lc-btn lc-btn--ghost"
            disabled={libelle.trim() === '' || enCours}
            onClick={() => { void ajouter() }}
          >
            <Plus size={15} aria-hidden="true" /> Ajouter ce libellé
          </button>
        </div>
      ) : null}
    </div>
  )
}

/* ═════════════════════════════ l'éditeur ══════════════════════════════ */

interface Brouillon {
  id: string | null
  valeurs: Record<string, string>
  actif: boolean
  usage: number | null
  brut: Record<string, unknown> | null
}

function valeursDe(champs: ChampDef[], brut: Record<string, unknown> | null): Record<string, string> {
  const out: Record<string, string> = {}
  for (const c of champs) out[c.cle] = brut === null ? '' : (texte(brut[c.cle]) ?? '')
  return out
}

function ReferenceEditor(props: {
  col: Collection
  champs: ChampDef[]
  contexte: Contexte
  initial: Brouillon
  peutEcrire: boolean
  onEnregistre: () => void
  onFerme: () => void
}) {
  const toast = useToast()
  const confirmer = useConfirm()
  const [valeurs, setValeurs] = useState(props.initial.valeurs)
  const [actif, setActif] = useState(props.initial.actif)

  const creation = props.initial.id === null
  const parCode = props.col.cle === 'code'
  // Un identifiant cité par au moins une écriture ne se renomme plus. On le
  // fige ICI plutôt que de laisser saisir un renommage que le serveur refuse :
  // un champ modifiable qui échoue toujours est un piège, pas une garde.
  const fige = !creation && (props.initial.usage ?? 0) > 0
  const manquant = props.champs.find((c) => c.requis === true && (valeurs[c.cle] ?? '').trim() === '')
  const dirty = props.champs.some((c) => (valeurs[c.cle] ?? '') !== (props.initial.valeurs[c.cle] ?? ''))
    || actif !== props.initial.actif

  useSaveBar({
    // Sans le droit d'écrire, aucune barre : proposer d'enregistrer ce que le
    // serveur refusera est une promesse qu'on ne peut pas tenir.
    dirty: props.peutEcrire && dirty,
    saveLabel: creation ? 'Créer' : 'Enregistrer',
    invalid: manquant !== undefined ? `« ${manquant.label} » est obligatoire.` : null,
    successMessage: creation ? 'Élément créé.' : 'Modifications enregistrées.',
    onSave: async () => {
      if (props.col.segment === null) return 'Cette collection est en lecture seule.'
      const corps: Record<string, unknown> = { active: actif }
      for (const c of props.champs) {
        const v = (valeurs[c.cle] ?? '').trim()
        // Un champ facultatif vidé vaut `null` : la chaîne vide serait un
        // libellé blanc, pas une absence.
        corps[c.cle] = v === '' ? null : (c.majuscules === true ? v.toUpperCase() : v)
      }
      if (props.initial.id !== null) {
        if (parCode) {
          // L'identité d'une nomenclature EST son code. Le champ saisi devient
          // une DEMANDE de renommage ; l'ancien code reste l'adresse de la
          // ligne à modifier — sans quoi le serveur ne saurait pas laquelle.
          const saisi = corps['code']
          corps['code'] = props.initial.id
          if (typeof saisi === 'string' && saisi !== props.initial.id) corps['new_code'] = saisi
        } else {
          corps['id'] = props.initial.id
        }
      }
      const echec = await mutation('POST', `/api/v1/finance/references/${props.col.segment}`, corps)
      if (echec === null) { props.onEnregistre(); return true }
      return echec
    },
    onCancel: () => { setValeurs(props.initial.valeurs); setActif(props.initial.actif) },
  })

  const archiver = async () => {
    if (props.initial.id === null || props.col.archiveSegment === null) return
    const nom = valeurs['name'] ?? valeurs['label'] ?? valeurs['code'] ?? ''
    const cites = props.initial.usage
    const ok = await confirmer({
      title: `Archiver « ${nom} » ?`,
      message: parCode
        ? 'Le code disparaît des listes de saisie et reste lisible partout où il a servi. Gestion ne remet PAS un code archivé en service : ce retrait est définitif côté application.'
        : 'L’élément disparaît des listes de saisie mais reste lisible dans toutes les clôtures passées. Rien n’est supprimé, et l’opération se relit dans l’audit.',
      ...(cites !== null ? { details: [{ label: 'Écritures qui le citent', value: String(cites) }] } : {}),
      variant: 'sensitive',
      confirmLabel: 'Archiver',
    })
    if (!ok) return
    const echec = await mutation(
      'POST', `/api/v1/finance/references/${props.col.archiveSegment}/${props.initial.id}/archive`,
    )
    if (echec !== null) { toast('error', echec); return }
    toast('ok', 'Élément archivé. Il reste lisible partout où il a servi.')
    props.onEnregistre()
    props.onFerme()
  }

  const section = props.col.alias !== undefined && props.initial.brut !== null
    ? props.col.alias(props.initial.brut, props.contexte)
    : null

  // Calculé ici plutôt que dans le rendu : `initial.brut` est nul à la création,
  // et TypeScript ne suit pas la garde à travers deux appels successifs.
  const identifiantTechnique = !creation && props.initial.brut !== null && props.col.technique !== undefined
    ? props.col.technique(props.initial.brut)
    : null

  return (
    <aside className="fr-editor">
      <div className="fr-editor__head">
        <div>
          <div className="fr-editor__title">{creation ? `Nouveau — ${props.col.titre}` : (valeurs['name'] ?? valeurs['label'] ?? 'Modifier')}</div>
          <div className="fr-editor__sub">
            {props.peutEcrire
              ? (creation ? 'Créez l’élément, puis enregistrez depuis la barre du bas.' : 'Les modifications s’enregistrent depuis la barre du bas.')
              : 'Lecture seule : vos droits ne permettent pas de modifier ce référentiel.'}
          </div>
        </div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={props.onFerme}>Fermer</button>
      </div>

      <div className="fr-fields">
        {props.champs.map((c) => {
          const brute = valeurs[c.cle] ?? ''
          const verrouille = c.figeSiUtilise === true && fige
          const lisible = c.type === 'liste'
            ? (c.options ?? []).find((o) => o.value === brute)?.label ?? null
            : (brute === '' ? null : brute)
          const aide = verrouille
            ? `Figé : ${props.initial.usage} écriture(s) citent déjà cet identifiant. Le libellé, lui, se modifie librement.`
            : c.aide
          return (
            <div key={c.cle} className="fr-champ">
            <Field label={c.label} helper={aide}>
              {!props.peutEcrire || verrouille ? (
                <span className="fr-fact__value">{lisible ?? '—'}</span>
              ) : c.type === 'liste' ? (
                <Select
                  value={brute}
                  options={c.options ?? []}
                  placeholder={c.requis === true ? 'À choisir' : (c.vide ?? 'Aucun')}
                  onChange={(v) => setValeurs((x) => ({ ...x, [c.cle]: v }))}
                  ariaLabel={c.label}
                />
              ) : (
                <TextInput
                  value={brute}
                  onChange={(v) => setValeurs((x) => ({ ...x, [c.cle]: v }))}
                  ariaLabel={c.label}
                  {...(c.exemple !== undefined ? { placeholder: c.exemple } : {})}
                />
              )}
            </Field>
            {/* Le chemin vers le référentiel source vient APRÈS l'explication du
                champ : glissé entre les deux, il coupait la phrase en deux et
                se lisait comme une légende du sélecteur. */}
            {c.lienGestion !== undefined && props.peutEcrire ? (
              <Link className="fr-lienref" href={c.lienGestion.route}>
                <Settings2 size={13} aria-hidden="true" /> {c.lienGestion.label}
              </Link>
            ) : null}
            </div>
          )
        })}
        <SwitchRow
          title="Actif"
          desc={props.col.id === 'pos'
            ? 'Un point de vente inactif n’est plus proposé dans les nouvelles clôtures, mais reste dans l’historique.'
            : 'Un élément inactif ne se propose plus à la saisie ; il reste lisible dans l’historique.'}
          checked={actif}
          onChange={setActif}
          disabled={!props.peutEcrire}
        />
        {/* L'identifiant technique n'est pas une question posée : il est
            fabriqué. On le montre après coup, replié, parce qu'il voyage dans
            les exports — et pas dans le tableau principal, où il volerait la
            place aux informations que quelqu'un lit vraiment. */}
        {identifiantTechnique !== null ? (
          <details className="fr-technique">
            <summary>Informations techniques</summary>
            <div className="fr-technique__corps">
              <span className="fr-technique__label">Identifiant technique</span>
              <code className="fr-technique__code">{identifiantTechnique}</code>
              <p className="fr-mini">
                Généré à la création, il ne change plus : renommer ce point de vente ou le
                déplacer ne le modifie pas. Il apparaît dans les exports.
              </p>
            </div>
          </details>
        ) : null}
      </div>

      {section !== null ? (
        <AliasFournisseur section={section} peutEcrire={props.peutEcrire} onChange={props.onEnregistre} />
      ) : null}

      {props.col.archiveSegment !== null && !creation && props.peutEcrire ? (
        <div className="fr-actions">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void archiver() }}>
            <Archive size={15} aria-hidden="true" /> Archiver
          </button>
          <span className="fr-mini">Jamais supprimer : une clôture passée doit rester lisible.</span>
        </div>
      ) : null}
    </aside>
  )
}

/* ═══════════════════════════════ la page ═══════════════════════════════ */

/**
 * L'ONGLET VIT DANS L'ADRESSE (`?onglet=caisses`). « Gérer les lieux » mène à
 * l'Organisation ; au Retour, l'onglet en mémoire était perdu et l'on
 * retombait sur les points de vente. Un onglet inconnu ouvre le premier.
 */
function ongletDepuisUrl(): string {
  const o = new URLSearchParams(window.location.search).get('onglet')
  return COLLECTIONS.some((c) => c.id === o) ? o! : COLLECTIONS[0]!.id
}

export function FinanceReferencesPage() {
  const { permissions, loading } = usePermissions()
  const [onglet, setOnglet] = useState(ongletDepuisUrl)
  const [archives, setArchives] = useState(false)
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null)

  const col = COLLECTIONS.find((c) => c.id === onglet) ?? COLLECTIONS[0]!
  const modifiable = col.segment !== null
  const peutEcrire = !loading && modifiable && permissions.includes(col.permission)

  // UNE requête pour six collections : l'API les sert ensemble parce que les
  // écrans de clôture en ont besoin ensemble. `with_usage` n'est demandé que
  // par cet écran : compter les citations balaie les lignes de clôture, et
  // l'assistant de clôture, lui, lit ce référentiel à chaque étape.
  // Les comptes bancaires en sont exclus — ils relèvent de `finance.bank.read`,
  // et les inclure aurait fait fuiter un IBAN masqué vers quiconque peut
  // consulter la liste des caisses.
  const refs = useRessource(
    `/api/v1/finance/references?include_archived=${archives}&include_inactive=true&with_usage=true`,
    lireReferentiels,
  )
  const comptes = useRessource(
    `/api/v1/finance/references/bank-accounts?include_archived=${archives}`,
    lireComptes,
  )
  // Les LIEUX ne sont pas un référentiel de la Comptabilité : ils appartiennent
  // au référentiel canonique de Gestion. La route ne rend que les lieux ACTIFS,
  // parce qu'un point de vente ne peut pas être créé dans un lieu désactivé.
  const lieux = useRessource('/api/v1/finance/references/locations', lireLieux)

  const etatCourant = col.racine === 'bank_accounts' ? comptes.etat : refs.etat
  const donnees = refs.etat.phase === 'ok' ? refs.etat.data : null
  const elements: Element[] = col.racine === 'bank_accounts'
    ? (comptes.etat.phase === 'ok' ? comptes.etat.data : [])
    : (donnees?.collections[col.racine] ?? [])

  const contexte: Contexte = useMemo(() => {
    const pdv = (donnees?.collections['points_of_sale'] ?? [])
      .filter((e) => !e.archive)
      .map((e) => ({ value: e.id, label: texte(e.brut['name']) ?? e.id }))
    const moyens = (donnees?.collections['payment_methods'] ?? [])
      .filter((e) => !e.archive)
      .map((e) => ({ value: e.id, label: texte(e.brut['label']) ?? e.id }))
    const lieuDuPos = new Map((donnees?.collections['points_of_sale'] ?? [])
      .map((e) => [e.id, texte(e.brut['location_name'])]))
    const caisses = (donnees?.collections['registers'] ?? [])
      .filter((e) => !e.archive)
      .map((e) => {
        const nom = texte(e.brut['name']) ?? e.id
        const pv = texte(e.brut['point_of_sale_name'])
        const lieu = lieuDuPos.get(texte(e.brut['point_of_sale_id']) ?? '') ?? null
        return { value: e.id, label: [nom, pv, lieu].filter(Boolean).join(' · ') }
      })
    return {
      lieux: lieux.etat.phase === 'ok' ? lieux.etat.data : [],
      pdv, moyens, caisses,
      aliasMoyens: donnees?.aliasMoyens ?? [],
      // L'écran des lieux appartient à l'Organisation, pas à la Comptabilité :
      // c'est `hr.org.read` qui décide, pas un droit Finance.
      peutVoirLieux: permissions.includes('hr.org.read'),
    }
  }, [donnees, lieux.etat, permissions])

  const champs = useMemo(() => col.champs(contexte), [col, contexte])

  const ouvrir = (e: Element) => setBrouillon({
    id: e.id, valeurs: valeursDe(champs, e.brut), actif: e.actif, usage: e.usage, brut: e.brut,
  })
  const creer = () => setBrouillon({
    id: null, valeurs: valeursDe(champs, null), actif: true, usage: null, brut: null,
  })
  const recharger = () => { refs.recharger(); comptes.recharger(); lieux.recharger() }

  // Changer d'onglet REMPLACE l'adresse : ce n'est pas une page de plus dans
  // l'historique, le Retour quitte les référentiels en un clic.
  useEffect(() => {
    remplacerRecherche('/comptabilite/referentiels', new URLSearchParams(onglet === COLLECTIONS[0]!.id ? {} : { onglet }))
  }, [onglet])

  return (
    <main className="lc-page lc-page--large lc-stagger">
      <SmartBack />

      <header className="fr-hero">
        <div>
          <h1 className="lc-title"><BookOpen size={26} aria-hidden="true" /> Référentiels</h1>
          <p className="lc-subtitle">
            Les objets que les clôtures nomment. Ils se créent et se renomment ;
            ils s’archivent — ils ne se suppriment jamais.
          </p>
        </div>
        <div className="fr-hero__actions">
          {peutEcrire ? (
            <button type="button" className="lc-btn lc-btn--primary" onClick={creer}>
              <Plus size={15} aria-hidden="true" /> Nouveau
            </button>
          ) : null}
        </div>
      </header>

      <div className="fr-tabscroll">
        <Tabs
          tabs={COLLECTIONS.map((c) => ({ key: c.id, label: c.onglet }))}
          active={onglet}
          onChange={(k) => { setOnglet(k); setBrouillon(null) }}
        />
      </div>

      <section className="fr-section">
        <div className="fr-section__head">
          <h2 className="fr-section__title">{col.titre}</h2>
          {col.archiveSegment !== null ? (
            <button
              type="button" className="lc-btn lc-btn--ghost lc-btn--sm"
              aria-pressed={archives}
              onClick={() => { setArchives((a) => !a); setBrouillon(null) }}
            >
              {archives ? 'Masquer les archivés' : 'Afficher les archivés'}
            </button>
          ) : null}
        </div>

        <div className="fr-note">
          <Info size={16} aria-hidden="true" />
          <span>{col.intro}</span>
        </div>

        {/*
          * Le second bandeau des TPE a été RETIRÉ.
          *
          * Il répétait la doctrine déjà portée par `intro` — préremplir n'est
          * pas décider — et il la répétait dans les termes d'un modèle qui
          * n'existe plus : la « préférence de point de vente » a laissé place à
          * une caisse habituelle. Deux encarts qui disent la même chose se
          * lisent une fois et demie, puis plus du tout, et celui qui reste
          * finit par mentir.
          */}

        {col.cle === 'code' ? (
          <div className="fr-note">
            <Info size={16} aria-hidden="true" />
            <span>
              <strong>Le libellé se change, l’identifiant se fige.</strong>{' '}
              Renommer « Chèque » en « Chèque bancaire » ne touche aucune écriture ;
              renommer le code CHEQUE ferait désigner par l’historique un code qui
              n’a jamais existé. Gestion le refuse dès la première citation.
            </span>
          </div>
        ) : null}

        <EtatBloc etat={etatCourant} quoi={`les ${col.titre.toLowerCase()}`} />

        {etatCourant.phase === 'ok' ? (
          <div className="fr-workbench">
            <div>
              {elements.length === 0 ? (
                <div className="fi-empty">
                  <div className="fi-empty__title">{col.videTitre ?? 'Aucun élément'}</div>
                  {col.videTexte ?? 'Ce référentiel est vide.'}{' '}
                  {peutEcrire ? (col.videTexte === undefined ? 'Créez le premier avec « Nouveau ».' : '') : modifiable ? 'Vos droits ne permettent pas d’en créer.' : 'Cette collection est livrée avec le produit.'}
                </div>
              ) : (
                <div className="fi-tablewrap">
                  <table className="fi-table">
                    <thead>
                      <tr>
                        {col.entetes.map((h) => <th key={h} scope="col">{h}</th>)}
                        <th scope="col">État</th>
                      </tr>
                    </thead>
                    <tbody>
                      {elements.map((e) => (
                        <tr
                          key={e.id}
                          className={modifiable ? `fr-row${brouillon?.id === e.id ? ' fr-row--on' : ''}` : undefined}
                          tabIndex={modifiable ? 0 : undefined}
                          role={modifiable ? 'button' : undefined}
                          aria-label={modifiable ? `Modifier ${texte(e.brut['name']) ?? texte(e.brut['label']) ?? e.id}` : undefined}
                          onClick={modifiable ? () => ouvrir(e) : undefined}
                          onKeyDown={modifiable
                            ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ouvrir(e) } }
                            : undefined}
                        >
                          {col.cellules(e.brut, contexte).map((c, i) => (
                            <td key={col.entetes[i] ?? String(i)} className={i === 0 ? 'fr-cell-main' : undefined}>
                              {c ?? <span className="fr-mini">—</span>}
                            </td>
                          ))}
                          <td>
                            {e.archive
                              ? <span className="lc-pill lc-pill--idle">Archivé</span>
                              : e.actif
                                ? <span className="lc-pill lc-pill--ok">Actif</span>
                                : <span className="lc-pill lc-pill--idle">Inactif</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {brouillon !== null && modifiable ? (
              <ReferenceEditor
                key={`${col.id}:${brouillon.id ?? 'nouveau'}`}
                col={col}
                champs={champs}
                contexte={contexte}
                initial={brouillon}
                peutEcrire={peutEcrire}
                onEnregistre={recharger}
                onFerme={() => setBrouillon(null)}
              />
            ) : (
              <aside className="fr-editor">
                <div className="fr-editor__head">
                  <div>
                    <div className="fr-editor__title">
                      {modifiable ? 'Aucun élément ouvert' : 'Collection en lecture seule'}
                    </div>
                    <div className="fr-editor__sub">
                      {modifiable
                        ? 'Choisissez une ligne pour la renommer, ou créez-en une nouvelle.'
                        : 'Aucune route d’écriture n’expose ces valeurs, et l’écran ne prétend pas le contraire.'}
                    </div>
                  </div>
                </div>
                <p className="fr-mini">
                  Rien ne se supprime ici. Un élément qui a servi à une clôture reste
                  lisible pour toujours : c’est ce qui permet de relire une nuit
                  d’il y a deux ans sans tomber sur un identifiant orphelin.
                </p>
              </aside>
            )}
          </div>
        ) : null}
      </section>
    </main>
  )
}
